import { useMemo, useCallback } from 'react';
import { isPhone, isWeb, navigateBack } from '@helpers';
import { filterSearchResultsForOperator } from '@config/operatorRestrictions';
import { role } from '@components/frame';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { useHandleImageNavigation } from '@v2/pages/common/hooks/useHandleImageNavigation';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { parseSuggestionItem, buildPopupModeItem, getNavigationParams, getAttachScopeId } from '@components/search/components/suggestion/helpers';

const useManageSuggestion = ({
  suggestion,
  setSuggestion,
  searchRef,
  width,
  top,
  scale,
  attachMode,
  attachImageId,
  updateImageMetadata,
  isPopupMode,
  onItemSelect,
}) => {
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();
  const { handleImageNavigation } = useHandleImageNavigation();
  const { IS_NARROW_WEB } = useDeviceSize();

  const filteredSuggestion = useMemo(() => filterSearchResultsForOperator(role, suggestion), [suggestion]);

  const shouldShowShadow = !isPhone || IS_NARROW_WEB;

  const getContainerStyle = useCallback(() => {
    if (isPopupMode) {
      return {
        width: '100%',
        marginTop: isWeb ? scale(60) : isPhone && IS_NARROW_WEB ? scale(37) : scale(60),
        position: 'absolute',
        paddingHorizontal: scale(20),
      };
    }

    if ((isPhone && IS_NARROW_WEB) || (!isWeb && isPhone)) {
      return { width: '100%', marginTop: scale(8) };
    }

    return {
      flex: 1,
      marginTop: scale(7),
      position: 'absolute',
      top: scale(top ? top : isWeb || isPhone ? 140 : 0),
      justifyContent: 'center',
      alignContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      left: scale(0),
      right: scale(0),
    };
  }, [isPopupMode, IS_NARROW_WEB, scale, top]);

  const attachItemToImage = useCallback(
    (itemType, itemId, itemName) => {
      if (!attachImageId) return;

      const scopeId = getAttachScopeId(itemType);
      if (scopeId == null) {
        console.warn('Unknown item type for attachment:', itemType);
        return;
      }

      updateImageMetadata({
        documentId: attachImageId,
        documentLinks: [
          {
            scopeId,
            scopeReferenceId: itemId,
            scopeReferenceName: itemName,
            isFavorite: false,
            isActivated: true,
            isFavoriteChanged: false,
          },
        ],
        documentTags: [],
      });

      setSuggestion([]);
      navigateBack();
    },
    [attachImageId, updateImageMetadata, setSuggestion],
  );

  const handleSuggestionPress = useCallback(
    (e, lookupKeyFields, text) => {
      searchRef.current.blur();
      if (e.metaKey) return;

      e.preventDefault();
      setSuggestion([]);

      const { id, type, itemName } = parseSuggestionItem(lookupKeyFields, text);

      if (isPopupMode && onItemSelect) {
        const item = buildPopupModeItem(type, id, itemName);
        return onItemSelect(item, type);
      }

      if (attachMode && attachImageId) {
        return attachItemToImage(type, id, itemName);
      }

      if (type === 'ImageDocument' || type === 'OtherDocument') {
        return handleImageNavigation({ documentId: id });
      }

      const navParams = getNavigationParams(type, id, itemName);
      if (navParams) {
        return navigateToMainDetailsScreen(navParams);
      }
    },
    [
      searchRef,
      setSuggestion,
      isPopupMode,
      onItemSelect,
      attachMode,
      attachImageId,
      attachItemToImage,
      handleImageNavigation,
      navigateToMainDetailsScreen,
    ],
  );

  return {
    filteredSuggestion,
    shouldShowShadow,
    getContainerStyle,
    handleSuggestionPress,
  };
};

export default useManageSuggestion;
